import { createClient } from '@supabase/supabase-js'
import type { Database } from '@/interfaces/database.types'
import type { ServiceAuthProps } from '@/interfaces/serviceAuth'
import { safeDecodeJwtPayload, summarizeClerkClaims } from '@/lib/jwt'

// Supabase configuration - using process.env (works in both Vite and Jest)
// Vite's define config maps environment variables to process.env for browser
// Jest natively supports process.env
const supabaseUrl = process.env.VITE_WAVE_SUPABASE_URL || 'http://localhost:54321'
const supabaseAnonKey = process.env.VITE_WAVE_SUPABASE_ANON_KEY || 'test-anon-key'

// Debug logging to verify environment variables are correctly loaded
console.log('🔍 Supabase config:', {
  env: process.env.NODE_ENV === 'test' ? 'jest' : 'browser',
  url: supabaseUrl,
  hasKey: !!supabaseAnonKey,
})

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

// Singleton pattern to prevent multiple GoTrueClient instances
let clerkSupabaseClient: ReturnType<typeof createClient<Database>> | null = null
let currentUserId: string | null = null

// Overloaded function to support both old session and new ServiceAuthProps
export function createClerkSupabaseClient(
  sessionProps: ServiceAuthProps
): ReturnType<typeof createClient<Database>>
export function createClerkSupabaseClient(
  session: unknown
): ReturnType<typeof createClient<Database>>
export function createClerkSupabaseClient(
  sessionOrProps: unknown
): ReturnType<typeof createClient<Database>> {
  // Detect if this is new ServiceAuthProps format or old session format
  const isStableProps =
    sessionOrProps &&
    typeof sessionOrProps === 'object' &&
    'userId' in sessionOrProps &&
    'getToken' in sessionOrProps

  if (isStableProps) {
    // New format: ServiceAuthProps
    const sessionProps = sessionOrProps as ServiceAuthProps
    const userId = sessionProps.userId || 'anonymous'

    // Reuse existing client if same user
    if (clerkSupabaseClient && currentUserId === userId) {
      return clerkSupabaseClient
    }

    // Create new client and cache it - Using NEW native Clerk integration
    clerkSupabaseClient = createClient<Database>(supabaseUrl, supabaseAnonKey, {
      async accessToken() {
        const token = await sessionProps.getToken()
        try {
          const payload = safeDecodeJwtPayload(token)
          const summary = summarizeClerkClaims(payload)
          console.log(
            '🔍 Supabase accessToken (stable props) token present:',
            !!token,
            'claims:',
            summary
          )
        } catch (e) {
          console.log('🔍 Supabase accessToken (stable props) token present:', !!token)
        }
        return token ?? null
      },
      auth: {
        autoRefreshToken: false,
        persistSession: false,
        detectSessionInUrl: false,
      },
    })

    currentUserId = userId
    return clerkSupabaseClient
  } else {
    // Old format: Clerk session object
    const session = sessionOrProps as any
    const sessionId = session?.id || 'no-session'

    // Reuse existing client if same session
    if (clerkSupabaseClient && currentUserId === sessionId) {
      return clerkSupabaseClient
    }

    // Create new client and cache it - Using NEW native Clerk integration
    clerkSupabaseClient = createClient<Database>(supabaseUrl, supabaseAnonKey, {
      async accessToken() {
        const token = await session?.getToken()
        try {
          const payload = safeDecodeJwtPayload(token)
          const summary = summarizeClerkClaims(payload)
          console.log(
            '🔍 Supabase accessToken (legacy) token present:',
            !!token,
            'claims:',
            summary
          )
        } catch (e) {
          console.log('🔍 Supabase accessToken (legacy) token present:', !!token)
        }
        return token ?? null
      },
      auth: {
        autoRefreshToken: false,
        persistSession: false,
        detectSessionInUrl: false,
      },
    })

    currentUserId = sessionId
    return clerkSupabaseClient
  }
}

// Only export the Clerk-integrated client function - no default client needed
